// apps/web/src/components/Sidebar/Sidebar.tsx
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthUser } from '../../auth/useAuthUser';
import { computeDailyReview } from '../../shared/useDailyReview';
import { capCount } from '../../shared/capCount';
import styles from './Sidebar.module.scss';

type NavItem = {
	label: string;
	to: string;
	icon: React.ReactNode;
};

// Learn and Impact Lab are expandable groups; everything else stays a flat
// top-level link. Sub-items are plain label/route pairs (they inherit the
// group's icon column indent, so they don't carry their own icons).
// `anonState` controls how the row renders for a logged-out visitor:
//   'open'   — normal link (default when omitted)
//   'locked' — greyed + 🔒, not navigable, honest "sign up to unlock"
//   'hidden' — not rendered at all
// Signed-in users always see every row as a normal link.
type AnonState = 'open' | 'locked' | 'hidden';
type SubItem = { label: string; to: string; anonState?: AnonState };

const learnSubItems: SubItem[] = [
	{ label: 'Learn Home', to: '/learn', anonState: 'open' },
	{ label: 'Core Path', to: '/learn/map', anonState: 'open' },
	{ label: 'Schools of Thought', to: '/learn/schools', anonState: 'open' },
	{ label: 'Playlists', to: '/learn/playlists', anonState: 'open' },
	{ label: 'Decks', to: '/learn/decks', anonState: 'locked' },
	{ label: 'Daily Review', to: '/review', anonState: 'open' },
];

// Routes that mean "Learn is the active area" — used to auto-open the group
// and light up the parent row.
const learnRoutePrefixes = ['/learn', '/review'];

// Impact Lab sub-items. No anonState set, so both default to 'open' for anon
// (their routes are public). Add anonState: 'locked' | 'hidden' here if you
// want to gate either row for logged-out visitors.
const impactSubItems: SubItem[] = [
	{ label: 'Scenarios', to: '/impact-lab' },
	{ label: 'Market Replay', to: '/impact-lab/market-replay' },
];

const impactRoutePrefixes = ['/impact-lab'];

const learnIcon = (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.6'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M2 5l8-3 8 3-8 3-8-3z' />
		<path d='M18 5v6' />
		<path d='M6 7.5V14c0 1.1 1.8 2 4 2s4-.9 4-2V7.5' />
	</svg>
);

const impactIcon = (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.6'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M8 2h4' />
		<path d='M9 2v5L4.5 15a1.5 1.5 0 001.3 2.3h8.4a1.5 1.5 0 001.3-2.3L11 7V2' />
		<path d='M6.5 12h7' />
	</svg>
);

// Top-level items that sit ABOVE the Learn group, in order.
const itemsBeforeLearn: NavItem[] = [
	{
		label: 'Home',
		to: '/',
		icon: (
			<svg
				viewBox='0 0 20 20'
				fill='none'
				stroke='currentColor'
				strokeWidth='1.6'
				strokeLinecap='round'
				strokeLinejoin='round'>
				<path d='M3 9.5L10 3l7 6.5V17a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z' />
				<path d='M7 18v-6h6v6' />
			</svg>
		),
	},
	{
		label: 'My Dashboard',
		to: '/dashboard',
		icon: (
			<svg
				viewBox='0 0 20 20'
				fill='none'
				stroke='currentColor'
				strokeWidth='1.6'
				strokeLinecap='round'
				strokeLinejoin='round'>
				<rect
					x='2'
					y='2'
					width='7'
					height='9'
					rx='1.5'
				/>
				<rect
					x='11'
					y='2'
					width='7'
					height='5'
					rx='1.5'
				/>
				<rect
					x='11'
					y='9'
					width='7'
					height='9'
					rx='1.5'
				/>
				<rect
					x='2'
					y='13'
					width='7'
					height='5'
					rx='1.5'
				/>
			</svg>
		),
	},
	{
		label: 'Simulate',
		to: '/simulate',
		icon: (
			<svg
				viewBox='0 0 20 20'
				fill='none'
				stroke='currentColor'
				strokeWidth='1.6'
				strokeLinecap='round'
				strokeLinejoin='round'>
				<path d='M2 14l4-5 3 3 4-6 5 4' />
				<path d='M2 18h16' />
			</svg>
		),
	},
];

// Top-level items that sit BELOW the Impact Lab group, in order.
const itemsAfterImpact: NavItem[] = [
	{
		label: 'Quizzes',
		to: '/quizzes',
		icon: (
			<svg
				viewBox='0 0 20 20'
				fill='none'
				stroke='currentColor'
				strokeWidth='1.6'
				strokeLinecap='round'
				strokeLinejoin='round'>
				<path d='M11 2 4.5 11h4l-1 7L14 9h-4l1-7z' />
			</svg>
		),
	},
	{
		label: 'Settings',
		to: '/settings',
		icon: (
			<svg
				viewBox='0 0 20 20'
				fill='none'
				stroke='currentColor'
				strokeWidth='1.6'
				strokeLinecap='round'
				strokeLinejoin='round'>
				<circle
					cx='10'
					cy='10'
					r='2.5'
				/>
				<path d='M10 2v1.5M10 16.5V18M2 10h1.5M16.5 10H18M4.1 4.1l1.1 1.1M14.8 14.8l1.1 1.1M4.1 15.9l1.1-1.1M14.8 5.2l1.1-1.1' />
			</svg>
		),
	},
];

const COLLAPSE_KEY = 'nisk.sidebar.collapsed';
const LEARN_OPEN_KEY = 'nisk.sidebar.learnOpen';
const IMPACT_OPEN_KEY = 'nisk.sidebar.impactOpen';

// Anonymous visitors reach exactly these top-level destinations; every
// other entry renders greyed + locked. The lock is honest, not hidden —
// the map shows what an account unlocks.
const ANON_ALLOWED = new Set([
	'/',
	'/simulate',
	'/impact-lab',
	'/learn',
	'/quizzes',
]);

// Shared renderer for a flat top-level item. Locks itself for anonymous
// sessions when the destination sits behind the login wall.
const TopLevelItem: React.FC<{ item: NavItem; collapsed: boolean }> = ({
	item,
	collapsed,
}) => {
	const { user, loading } = useAuthUser();
	const locked = !loading && user === null && !ANON_ALLOWED.has(item.to);

	if (locked) {
		return (
			<span
				className={`${styles.navItem} ${styles.navLocked}`}
				aria-disabled='true'
				title={
					collapsed
						? `${item.label} — sign up to unlock`
						: 'Sign up to unlock'
				}>
				<span className={styles.navIcon}>{item.icon}</span>
				<span className={styles.navLabel}>{item.label}</span>
				<span
					className={styles.lockMark}
					aria-hidden='true'>
					🔒
				</span>
			</span>
		);
	}

	return (
		<NavLink
			to={item.to}
			end={item.to === '/'}
			className={({ isActive }) =>
				`${styles.navItem} ${isActive ? styles.active : ''}`
			}
			title={collapsed ? item.label : undefined}>
			<span className={styles.navIcon}>{item.icon}</span>
			<span className={styles.navLabel}>{item.label}</span>
			<span
				className={styles.activeBar}
				aria-hidden='true'
			/>
		</NavLink>
	);
};

// Shared expandable group. Mirrors the original Learn group exactly; the
// only per-group variation is which sub-item counts as "active" for a given
// pathname (Impact Lab's "Scenarios" row must NOT light up on the Market
// Replay routes, even though both live under /impact-lab).
const NavGroup: React.FC<{
	id: string;
	label: string;
	icon: React.ReactNode;
	subItems: SubItem[];
	open: boolean;
	onToggle: () => void;
	parentActive: boolean;
	isSubActive: (sub: SubItem, pathname: string) => boolean;
	/** Optional per-route count pills (e.g. the Daily Review ready count). */
	badges?: Record<string, number>;
	/** Logged-out session — drives per-sub-item lock/hide via anonState. */
	anonymous: boolean;
}> = ({
	id,
	label,
	icon,
	subItems,
	open,
	onToggle,
	parentActive,
	isSubActive,
	badges,
	anonymous,
}) => {
	const location = useLocation();
	return (
		<div className={styles.group}>
			<button
				type='button'
				className={`${styles.navItem} ${styles.groupToggle} ${
					parentActive ? styles.active : ''
				}`}
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={id}>
				<span className={styles.navIcon}>{icon}</span>
				<span className={styles.navLabel}>{label}</span>
				<span
					className={`${styles.chevron} ${
						open ? styles.chevronOpen : ''
					}`}
					aria-hidden='true'>
					<svg
						viewBox='0 0 20 20'
						fill='none'
						stroke='currentColor'
						strokeWidth='1.8'
						strokeLinecap='round'
						strokeLinejoin='round'>
						<path d='M6 8l4 4 4-4' />
					</svg>
				</span>
				<span
					className={styles.activeBar}
					aria-hidden='true'
				/>
			</button>

			{/* grid-template-rows 0fr→1fr animates the collapse smoothly.
			    The inner wrapper owns overflow:hidden so children clip as the
			    row height animates. `hidden` is gone (it forced display:none,
			    which kills the transition); the a11y tree is kept honest with
			    aria-hidden + inert-when-closed via tabIndex on the links. */}
			<div
				id={id}
				className={`${styles.subnav} ${open ? styles.subnavOpen : ''}`}
				role='group'
				aria-label={label}
				aria-hidden={!open}>
				<div className={styles.subnavInner}>
					{subItems.map((sub) => {
						const anonState: AnonState = anonymous
							? (sub.anonState ?? 'open')
							: 'open';

						// Hidden: skip the row entirely for anon.
						if (anonState === 'hidden') return null;

						// Locked: greyed, non-navigable, honest lock mark.
						if (anonState === 'locked') {
							return (
								<span
									key={sub.to}
									className={`${styles.subItem} ${styles.navLocked}`}
									aria-disabled='true'
									title='Sign up to unlock'>
									<span className={styles.subLabel}>
										{sub.label}
									</span>
									<span
										className={styles.lockMark}
										aria-hidden='true'>
										🔒
									</span>
								</span>
							);
						}

						return (
							<NavLink
								key={sub.to}
								to={sub.to}
								tabIndex={open ? 0 : -1}
								className={() =>
									`${styles.subItem} ${
										isSubActive(sub, location.pathname)
											? styles.subActive
											: ''
									}`
								}>
								<span className={styles.subLabel}>
									{sub.label}
								</span>
								{badges && (badges[sub.to] ?? 0) > 0 && (
									<span
										className={styles.subBadge}
										aria-label={`${badges[sub.to]} ready`}>
										{capCount(badges[sub.to] ?? 0)}
									</span>
								)}
							</NavLink>
						);
					})}
				</div>
			</div>
		</div>
	);
};

// Learn sub-items behave like plain NavLinks: exact match for the hub,
// prefix match for everything else.
const isLearnSubActive = (sub: SubItem, pathname: string): boolean => {
	if (sub.to === '/learn') return pathname === '/learn';
	return pathname === sub.to || pathname.startsWith(`${sub.to}/`);
};

// Impact Lab: "Scenarios" owns /impact-lab and every scenario deep link,
// EXCEPT the Market Replay subtree — that belongs to its own row.
const isImpactSubActive = (sub: SubItem, pathname: string): boolean => {
	if (sub.to === '/impact-lab') {
		const inImpact =
			pathname === '/impact-lab' || pathname.startsWith('/impact-lab/');
		const inReplay = pathname.startsWith('/impact-lab/market-replay');
		return inImpact && !inReplay;
	}
	return pathname === sub.to || pathname.startsWith(`${sub.to}/`);
};

// Ambient "review is ready" count for the Daily Review entry. Recomputed on
// the same-tab 'nis:progress' signal and cross-tab storage events — the same
// triggers useStreak uses — so the pill tracks lessons/reviews live. Soft
// product language on purpose: this is "ready when you are", never "overdue".
function useReviewReadyCount(): number {
	const [count, setCount] = useState<number>(
		() => computeDailyReview().totalReady,
	);
	useEffect(() => {
		const refresh = () => setCount(computeDailyReview().totalReady);
		window.addEventListener('storage', refresh);
		window.addEventListener('nis:progress', refresh);
		return () => {
			window.removeEventListener('storage', refresh);
			window.removeEventListener('nis:progress', refresh);
		};
	}, []);
	return count;
}

const Sidebar: React.FC = () => {
	const location = useLocation();
	const reviewReady = useReviewReadyCount();
	const { user: authUser, loading: authLoading } = useAuthUser();
	const anonymous = !authLoading && authUser === null;
	const [collapsed, setCollapsed] = useState<boolean>(false);

	const onLearnRoute = learnRoutePrefixes.some(
		(p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
	);
	const onImpactRoute = impactRoutePrefixes.some(
		(p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
	);

	// Group open state. Defaults open when on the group's route; otherwise
	// restores the user's last choice. Persisted across sessions.
	const [learnOpen, setLearnOpen] = useState<boolean>(onLearnRoute);
	const [impactOpen, setImpactOpen] = useState<boolean>(onImpactRoute);

	useEffect(() => {
		const savedCollapsed = localStorage.getItem(COLLAPSE_KEY);
		if (savedCollapsed !== null) setCollapsed(savedCollapsed === 'true');
		const savedLearn = localStorage.getItem(LEARN_OPEN_KEY);
		if (savedLearn !== null) setLearnOpen(savedLearn === 'true');
		const savedImpact = localStorage.getItem(IMPACT_OPEN_KEY);
		if (savedImpact !== null) setImpactOpen(savedImpact === 'true');
	}, []);

	useEffect(() => {
		localStorage.setItem(COLLAPSE_KEY, String(collapsed));
	}, [collapsed]);

	useEffect(() => {
		localStorage.setItem(LEARN_OPEN_KEY, String(learnOpen));
	}, [learnOpen]);

	useEffect(() => {
		localStorage.setItem(IMPACT_OPEN_KEY, String(impactOpen));
	}, [impactOpen]);

	// Navigating into a group's area auto-opens it so the active sub-item is
	// visible. Never force it closed on navigation away — that's the user's
	// choice to make.
	useEffect(() => {
		if (onLearnRoute) setLearnOpen(true);
	}, [onLearnRoute]);

	useEffect(() => {
		if (onImpactRoute) setImpactOpen(true);
	}, [onImpactRoute]);

	// When collapsed, sub-groups can't show labels — each group renders as a
	// single icon link to its hub instead of an expander.
	const showGroups = !collapsed;

	return (
		<aside
			className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}
			aria-expanded={!collapsed}>
			{/* ── Logo ── */}
			<div className={styles.header}>
				<span
					className={styles.logo}
					aria-hidden={collapsed}>
					<span className={styles.logoAccent}>Nisk</span>
				</span>
				<button
					type='button'
					className={styles.toggle}
					onClick={() => setCollapsed((c) => !c)}
					aria-label={
						collapsed ? 'Expand sidebar' : 'Collapse sidebar'
					}
					aria-pressed={collapsed}>
					<svg
						viewBox='0 0 20 20'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'>
						{collapsed ? (
							<path d='M7 5l5 5-5 5' />
						) : (
							<path d='M13 5l-5 5 5 5' />
						)}
					</svg>
				</button>
			</div>

			{/* ── Nav ── */}
			<nav
				className={styles.nav}
				aria-label='Primary'>
				{itemsBeforeLearn.map((item) => (
					<TopLevelItem
						key={item.to}
						item={item}
						collapsed={collapsed}
					/>
				))}

				{/* ── Learn group ── */}
				{/* Anon users get the expandable group too; per-sub-item
				    anonState drives which rows lock/hide. */}
				{showGroups ? (
					<NavGroup
						id='learn-subnav'
						label='Learn'
						icon={learnIcon}
						subItems={learnSubItems}
						open={learnOpen}
						onToggle={() => setLearnOpen((o) => !o)}
						parentActive={onLearnRoute}
						isSubActive={isLearnSubActive}
						badges={{ '/review': reviewReady }}
						anonymous={anonymous}
					/>
				) : (
					// Collapsed: Learn is a plain icon link to the hub.
					<TopLevelItem
						item={{ label: 'Learn', to: '/learn', icon: learnIcon }}
						collapsed={collapsed}
					/>
				)}

				{/* ── Impact Lab group ── */}
				{showGroups ? (
					<NavGroup
						id='impact-subnav'
						label='Impact Lab'
						icon={impactIcon}
						subItems={impactSubItems}
						open={impactOpen}
						onToggle={() => setImpactOpen((o) => !o)}
						parentActive={onImpactRoute}
						isSubActive={isImpactSubActive}
						anonymous={anonymous}
					/>
				) : (
					<TopLevelItem
						item={{
							label: 'Impact Lab',
							to: '/impact-lab',
							icon: impactIcon,
						}}
						collapsed={collapsed}
					/>
				)}

				{itemsAfterImpact.map((item) => (
					<TopLevelItem
						key={item.to}
						item={item}
						collapsed={collapsed}
					/>
				))}
			</nav>

			{/* ── Bottom version stamp ── */}
			{!collapsed && (
				<div className={styles.footer}>
					<span className={styles.version}>V1</span>
				</div>
			)}
		</aside>
	);
};

export default Sidebar;
